import { useEffect } from 'react'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import { useMediaQuery } from '@/lib/use-media-query'

/**
 * Preferences belong to the device, not the inventory (D34): a phone at the
 * shelf and a laptop at the desk want different densities, and Fundus has one
 * user, so there is nobody to share them with. They live in localStorage.
 */
export type Density = 'auto' | 'desk' | 'shelf'
export type HomeScreen = 'overview' | 'parts' | 'storage'

/** Below this width `auto` density means the shelf. */
export const SHELF_QUERY = '(max-width: 639px)'

export const DEFAULT_QUICK_TAKE = [1, 5, 10] as const

interface PreferencesState {
  density: Density
  home: HomeScreen
  labelProfileId: string | null
  quickTake: number[]
  setDensity: (density: Density) => void
  setHome: (home: HomeScreen) => void
  setLabelProfileId: (id: string | null) => void
  setQuickTake: (amounts: number[]) => void
}

export const PREFERENCES_KEY = 'fundus.preferences'

export const usePreferences = create<PreferencesState>()(
  persist(
    (set) => ({
      density: 'auto',
      home: 'overview',
      labelProfileId: null,
      quickTake: [...DEFAULT_QUICK_TAKE],
      setDensity: (density) => set({ density }),
      setHome: (home) => set({ home }),
      setLabelProfileId: (labelProfileId) => set({ labelProfileId }),
      setQuickTake: (quickTake) => set({ quickTake }),
    }),
    {
      name: PREFERENCES_KEY,
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: ({ density, home, labelProfileId, quickTake }) => ({
        density,
        home,
        labelProfileId,
        quickTake,
      }),
    },
  ),
)

/** The density in force: the preference, or the viewport's when it is `auto`. */
export function resolveDensity(density: Density, narrow: boolean): 'desk' | 'shelf' {
  if (density !== 'auto') return density
  return narrow ? 'shelf' : 'desk'
}

export function useEffectiveDensity(): 'desk' | 'shelf' {
  const density = usePreferences((state) => state.density)
  const narrow = useMediaQuery(SHELF_QUERY)
  return resolveDensity(density, narrow)
}

/**
 * Mirror the effective density onto <html data-density>, where the stylesheet
 * turns it into control heights. `force` pins it, for the shelf screens.
 */
export function useDensityAttribute(force?: 'desk' | 'shelf') {
  const effective = useEffectiveDensity()
  const density = force ?? effective
  useEffect(() => {
    document.documentElement.dataset.density = density
  }, [density])
}
